import { useState, useEffect, useRef, useCallback, SyntheticEvent, MouseEvent } from 'react';

import BasicTableProps, { Label, RowItemData } from './type';
import { Box } from '../../Box';
import Icon from '../../Icon';
import TablePagination from '../TablePagination';
import Typography from '../../Typography';
import { SkeletonRow } from '../Rows';
import { useWindowSize } from '../../hooks';
import { getUniqueKey } from '../../utils';
import {
  HeaderStyled,
  HeaderDataContainerStyled,
  EmptyTableRowStyled,
  TableContainerStyled,
  TableDataContainerStyled,
  TableContentContainerStyled,
  EmptyTableRowsContainerStyled,
  NoResultsFoundContainerStyled,
} from './styled';

const BasicTable = <T,>({ ...props }: BasicTableProps<T>) => {
  const {
    data,
    emptyLabel,
    labels,
    total,
    rowsPerPage,
    page,
    notifyParent,
    RowController,
    fetchOnPageChange,
    rowControllerProps,
    isSortable,
    orderBy: orderByProp,
    loading,
    style: tableStyleProp,
    ...rest
  } = props;
  const [rowItems, setRowItems] = useState<RowItemData>({});
  const [loadingData, setLoadingData] = useState<boolean>(loading);
  const [orderBy, setOrderBy] = useState<string | null>(orderByProp);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [rowHeight, setRowHeight] = useState<number>(0);
  const { isMobile } = useWindowSize({ mobile: 700 });

  const tableRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const minWidth = 75;

  const getDefaultColumnWidth = useCallback(() => {
    const rawTableWidth = tableRef.current?.offsetWidth || 0;
    const totalFixedWidth = labels.reduce((sum, { width }) => sum + (width || 0), 0);
    const flexibleColumnsCount = labels.filter(({ width }) => !width).length;
    const tableWidth =
      rawTableWidth < totalFixedWidth
        ? totalFixedWidth + minWidth * flexibleColumnsCount
        : rawTableWidth;
    const remainingWidth = tableWidth - totalFixedWidth;
    if (flexibleColumnsCount === 0) return minWidth;
    const defaultWidth = remainingWidth / flexibleColumnsCount;
    return defaultWidth > minWidth - 24 ? defaultWidth : minWidth;
  }, [labels]);

  const getSliceToShow = (): [number, number] => {
    const pageLimitIndicator = rowsPerPage > total ? total : rowsPerPage;
    if (fetchOnPageChange) return [0, pageLimitIndicator];
    return [page * pageLimitIndicator, page * pageLimitIndicator + pageLimitIndicator];
  };

  const getEmptyRows = (): number => {
    const dataLimit = Math.min(data.length, total);
    if (fetchOnPageChange) return Math.max(0, rowsPerPage - dataLimit);
    return Math.max(0, Math.min(rowsPerPage, (1 + page) * rowsPerPage - dataLimit));
  };

  const handlePageChange = (__event: SyntheticEvent<HTMLButtonElement>, newPage: number) => {
    notifyParent({ type: 'changePage', data: { newPage, rowsPerPage } });
  };

  const sortHandler = (__event: MouseEvent<HTMLDivElement>, property: string) => {
    setOrder((oldOrder) => (orderBy === property && oldOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy(property);
    notifyParent({ type: 'sort', data: { order, orderBy: property } });
  };

  useEffect(() => {
    const defaultColumnWidth = getDefaultColumnWidth();
    setRowItems(
      labels.reduce((previous, { value: currentValue, width }) => {
        return {
          ...previous,
          [currentValue]: { value: currentValue, width: width || defaultColumnWidth },
        };
      }, {}),
    );
  }, [labels]);

  useEffect(() => {
    if (loading) setLoadingData(true);
  }, [loading]);

  useEffect(() => {
    if (!loading) setLoadingData(false);
  }, [data, loading]);

  useEffect(() => {
    if (tableContentRef.current && tableHeaderRef.current) {
      const contentHeight = tableContentRef.current.clientHeight || 0;
      const headerHeight = tableHeaderRef.current.clientHeight || 0;
      setRowHeight((contentHeight - headerHeight) / rowsPerPage);
    }
  }, [tableContentRef.current, tableHeaderRef.current, loading]);

  const renderLabels = () => {
    return (
      <HeaderStyled ref={tableHeaderRef}>
        {labels.map((label: Label) => {
          const { name, value, style = {}, sortable = false } = label || {};
          return (
            <HeaderDataContainerStyled
              key={value}
              width={rowItems[value]?.width}
              style={{ ...style }}
              isSortable={isSortable}
              sortable={sortable}
              {...(isSortable && sortable && { onClick: (event) => sortHandler(event, value) })}
            >
              <Typography
                fontSize={12}
                style={{
                  lineHeight: 1.2,
                  ...(isSortable && sortable && orderBy === value && { color: '#1F89FF' }),
                }}
              >
                {name}
              </Typography>
              {isSortable && sortable && orderBy === value && (
                <Box>
                  {order === 'asc' ? (
                    <Icon name="northOutlined" color="#1F89FF" size="12px" />
                  ) : (
                    <Icon name="southOutlined" color="#1F89FF" size="12px" />
                  )}
                </Box>
              )}
            </HeaderDataContainerStyled>
          );
        })}
      </HeaderStyled>
    );
  };

  const renderTableBody = () => {
    const emptyRows = getEmptyRows();
    if (loadingData) {
      return [...Array(rowsPerPage)].map((_, index) => (
        <SkeletonRow
          data-testid={`skeleton-row-${index}`}
          height={rowHeight}
          items={rowItems}
          key={getUniqueKey()}
        />
      ));
    }
    return (
      <div>
        {[...data].slice(...getSliceToShow()).map((rowItem, index) => {
          const itemId = getUniqueKey();
          return (
            <RowController
              key={itemId}
              data={rowItem}
              items={rowItems}
              notifyParent={notifyParent}
              rowControllerProps={rowControllerProps}
              style={{
                ...((rowsPerPage <= total ? rowsPerPage - 1 : total - 1) !== index && {
                  borderBottom: '1px solid #667085',
                }),
              }}
            />
          );
        })}
        {emptyRows > 0 && total !== 0 && (
          <EmptyTableRowStyled rowHeight={rowHeight} emptyRows={emptyRows}>
            <Box />
          </EmptyTableRowStyled>
        )}
      </div>
    );
  };

  return (
    <TableContainerStyled
      ref={tableRef}
      style={{
        ...(labels.filter(({ width }) => !width).length === 0 && !isMobile
          ? {
              width: `${labels.reduce((sum, { width }) => sum + (width || 0), 0)}px`,
            }
          : {}),
        ...tableStyleProp,
      }}
      {...rest}
    >
      <TableContentContainerStyled ref={tableContentRef}>
        <TableDataContainerStyled>
          {renderLabels()}
          {(data?.length !== 0 || loadingData) && renderTableBody()}
        </TableDataContainerStyled>
        {(data?.length === 0 || total === 0) && !loadingData && (
          <EmptyTableRowsContainerStyled rowHeight={rowHeight} emptyRows={getEmptyRows()}>
            <Box>{emptyLabel}</Box>
          </EmptyTableRowsContainerStyled>
        )}
      </TableContentContainerStyled>
      {rowsPerPage < total && (
        <TablePagination
          rowsPerPageOptions={[rowsPerPage]}
          total={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
        />
      )}
    </TableContainerStyled>
  );
};

BasicTable.defaultProps = {
  data: null,
  emptyLabel: (
    <NoResultsFoundContainerStyled>
      <Typography variant="h5" weight="semi-bold">
        No encontramos resultados que coincidan con tu búsqueda.
      </Typography>
      <Typography>Prueba ajustando los filtros o ampliando los criterios</Typography>
    </NoResultsFoundContainerStyled>
  ),
  page: 0,
  total: 0,
  labels: [],
  rowsPerPage: 20,
  notifyParent: () => {},
  fetchOnPageChange: false,
  rowControllerProps: {},
  isSortable: false,
  loading: false,
};

export default BasicTable;
